/**
 * useUsersSectionPrefetch - Прогрев кэша соседних вкладок раздела «Пользователи»
 * ================================================================================
 *
 * Вкладки раздела (Регистрации/DAU-MAU/Доходы/ARPDAU) размонтируются при
 * переключении (ContentArea рендерит их через switch), поэтому без прогрева
 * каждый клик по вкладке заново бьёт в ClickHouse. Здесь дублируются ТЕ ЖЕ
 * вызовы MetricsService, что делают сами виджеты, без обработки ответа —
 * ответ сам ляжет в клиентский TTL-кэш (metricsService.js), и виджет при
 * реальном монтировании получит его оттуда.
 *
 * Киты (WhalesWidget) сознательно НЕ прогреваются: запрос дорогой (p95 8.5s).
 */

import { useEffect, useRef } from 'react';
import { useFilters } from './useFilters';
import { MetricsService } from '../services/metricsService';
import { hasVersionFilter } from '../utils/versionFilter';

// groupBy читаем из тех же ключей localStorage, что и сам виджет-потребитель —
// иначе прогрев попадёт мимо кэша, если пользователь раньше переключил
// группировку на 'week'/'month'.
function readGroupBy(storageKey) {
  try {
    const stored = localStorage.getItem(storageKey);
    return stored === 'week' || stored === 'month' ? stored : 'day';
  } catch {
    return 'day';
  }
}

export function useUsersSectionPrefetch(isActive) {
  const { filters } = useFilters();
  const lastKeyRef = useRef(null);

  useEffect(() => {
    if (!isActive) return;

    // Прогреваем заново только когда фильтры реально изменились — иначе
    // быстрый чейндж (например, драг по календарю) даёт по 4-6 гонок на
    // каждое промежуточное значение вместо одной на финальное.
    const key = JSON.stringify(filters);
    if (lastKeyRef.current === key) return;
    lastKeyRef.current = key;

    // ARPDAU и Доходы читают admon/inapps-by-period с календарной
    // фильтрацией (dateFilterType: 'calendar'), а не по когорте.
    const calendarFilters = { ...filters, dateFilterType: 'calendar' };

    // Регистрации: вкладка «Регистрации». С фильтром версии вкладка скрыта
    // (источник без колонок версий), поэтому не греем.
    if (!hasVersionFilter(filters.clientVersions, filters.installVersions)) {
      MetricsService.getRegistrations(filters, readGroupBy('registrations-widget-groupBy')).catch(() => {});
    }

    // DAU/MAU: вкладка «DAU/MAU» (некалендарные фильтры)
    MetricsService.getActiveUsers(filters, readGroupBy('active-users-widget-groupBy')).catch(() => {});

    // ARPDAU тоже дёргает getActiveUsers, но с календарной фильтрацией, своей
    // группировкой И includeLoyal: false — это отдельный ключ кэша и на
    // клиенте (includeLoyal попадает в URL), и на сервере (суффикс -noloyal,
    // см. A4 фазы плана). Без четвёртого аргумента прогрев наполнял бы ключ
    // с лояльными, которого вкладка ARPDAU никогда не спрашивает: лишний
    // запрос в ClickHouse и промах кэша на самой вкладке.
    MetricsService.getActiveUsers(
      calendarFilters,
      readGroupBy('arpdau-widget-groupBy'),
      {},
      { includeLoyal: false }
    ).catch(() => {});

    // admon/inapps-by-period нужны и вкладке «Доходы», и вкладке «ARPDAU» —
    // у них могут быть разные сохранённые группировки, поэтому прогреваем
    // все различающиеся варианты.
    const revenueGroupBys = new Set([
      readGroupBy('revenue-widget-groupBy'),
      readGroupBy('arpdau-widget-groupBy')
    ]);
    revenueGroupBys.forEach((groupBy) => {
      MetricsService.getAdMonByPeriod(calendarFilters, groupBy).catch(() => {});
      MetricsService.getInAppsByPeriod(calendarFilters, groupBy).catch(() => {});
    });
  }, [isActive, filters]);
}

export default useUsersSectionPrefetch;
